import React, { useEffect, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import { compareDesc, isAfter, isValid } from 'date-fns'

import { Box, Tabs, Tab, Typography } from '@mui/material'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'

import NewReleasesIcon from '@mui/icons-material/NewReleases'
import useAuth from '../../../hooks/useAuth'
import { CompanyTargets } from './CompanyTargets'
import { PRTabs } from './PRTabs'
import { IsinMap } from './IsinMap/IsinMap'
import CompanyApi from '../../../api/CompanyApi'
import IsinsApi from '../../../api/IsinsApi'
import PRInputsApi from '../../../api/PRInputsApi'
import { SubscriptionButton } from './SubscriptionButton'
import useUpdatesSubscription from '../../../hooks/useUpdatesSubscription'

function PRInput() {
    const { isAuthenticated } = useAuth()
    const [tabValue, setTabValue] = useState('pr')
    const [unusedIsins, setUnusedIsins] = useState([])
    const [filteredUnusedIsins, setFilteredUnusedIsins] = useState([])
    const [selectedTargets, setSelectedTargets] = useState([])
    // const [prs, setPrs] = React.useState(null)
    const [sourceType, setSourceType] = useState(null)
    const [companyNameSearch, setCompanyNameSearch] = useState('')
    const [selectedCompany, setSelectedCompany] = useState(null)
    const [selectedPR, setSelectedPR] = useState(null)
    const [prsInInbox, setPrsInInbox] = useState(null)
    const [prsArchived, setPrsArchived] = useState(null)
    const [companies, setCompanies] = useState(null)
    const [isinStartDate, setIsinStartDate] = useState(new Date('2022-01-01'))
    const [onlyIsinsWithEvents, setOnlyIsinsWithEvents] = useState(false)
    const [newIsins, setNewIsins] = useState(false)
    // associatedPrs is a map from target id to prs: { targetId: [pr1, pr2, ...]}

    const [associatedPRs, setAssociatedPRs] = useState({})
    const companyApi = new CompanyApi()
    const isinsApi = new IsinsApi()
    const prinputsApi = new PRInputsApi()
    const { clearCounter, updatesCount } = useUpdatesSubscription()

    const fetchPressReleases = React.useCallback(async () => {
        if (!isAuthenticated) return
        let result = (await prinputsApi.findAll('prinput')) || []
        // sort by published date
        // just use the first 30 sources for now
        result = result.sort((a, b) => (Date.parse(a.publishDate) < Date.parse(b.publishDate) ? 1 : -1))
        const inbox = result.filter((pr) => !pr.archivedAt)
        const shortenedInbox = inbox.slice(0, 100)
        setPrsInInbox(shortenedInbox)
        const archived = result.filter((pr) => pr.archivedAt)
        setPrsArchived(archived.slice(0, 100))
        clearCounter()
    }, [isAuthenticated])

    useEffect(() => {
        fetchPressReleases()
    }, [fetchPressReleases])

    useEffect(() => {
        if (updatesCount) {
            fetchPressReleases()
        }
    }, [updatesCount, fetchPressReleases])

    useEffect(() => {
        if (!isAuthenticated) return
        const fetchData = async () => {
            const result = await companyApi.findAll()
            result.sort((a, b) => a.name.localeCompare(b.name))
            setCompanies(result)

            const fetchedUnusedIsins = await isinsApi.fetchUnusedIsins()

            const startDate = new Date('2022-01-01')
            const newUnmatchedIsins = fetchedUnusedIsins.filter((unusedIsin) =>
                isAfter(new Date(unusedIsin.tradeFirstDate), startDate),
            )

            setNewIsins(newUnmatchedIsins.length > 0)
            setUnusedIsins(fetchedUnusedIsins)
        }
        fetchData()
    }, [isAuthenticated])

    useEffect(() => {
        if (isValid(isinStartDate) && isAfter(isinStartDate, new Date('2015-01-01'))) {
            const sortedAndFilteredUnusedIsins = unusedIsins
                .filter(
                    (unusedIsin) =>
                        isAfter(new Date(unusedIsin.tradeFirstDate), isinStartDate) &&
                        (onlyIsinsWithEvents
                            ? unusedIsin.instrumentType === 'warrant'
                                ? unusedIsin.companyWarrants && unusedIsin.companyWarrants.length > 0
                                : unusedIsin.companyIssues && unusedIsin.companyIssues.length > 0
                            : true),
                )
                .sort((a, b) => compareDesc(new Date(a.tradeFirstDate), new Date(b.tradeFirstDate)))

            setFilteredUnusedIsins(sortedAndFilteredUnusedIsins)
        }
    }, [isinStartDate, onlyIsinsWithEvents, unusedIsins])

    const handleArchive = async (pr) => {
        pr.archived = new Date().toISOString()
        await prinputsApi
            .archive(pr)
            .then(() => console.log(`Archived ${pr.url}`))
            .catch((error) => console.error(error))
        setPrsInInbox([...prsInInbox.toSpliced(prsInInbox.indexOf(pr), 1)])
        setPrsArchived([pr, ...prsArchived])
    }

    const handleDisconnectCompany = async (company) => {
        setSelectedCompany(null)
        setSelectedPR(null)
        await prinputsApi.disconnectCompany(company.id)
        const modifiedPRs = prsInInbox.map((pr) => {
            if (pr.companyId === company.id) {
                pr.companyId = null
                pr.companyName = null
            }
            return pr
        })
        setPrsInInbox(modifiedPRs)
    }

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue)
    }

    return (
        <>
            <Helmet title='PM Indata' />
            <Grid container>
                <Grid size={6}>
                    <Typography variant='h3' gutterBottom display='inline'>
                        PM Indata
                    </Typography>
                </Grid>
                <Grid size={6} sx={{ textAlign: 'right' }}>
                    <SubscriptionButton />
                </Grid>
            </Grid>

            <Box my={6} sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label='basic tabs example'>
                        <Tab label='Pressmeddelanden' value={'pr'} />
                        <Tab
                            label={
                                <Stack direction='row' alignItems='center' spacing={1}>
                                    <Typography alignSelf='flex-end'>ISIN</Typography>
                                    {newIsins ? <NewReleasesIcon color='warning' /> : null}
                                </Stack>
                            }
                            value={'isin'}
                        />
                    </Tabs>
                </Box>
            </Box>
            {tabValue === 'pr' ? (
                <Grid container spacing={6}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <PRTabs
                            // onAssociate={onAssociate}
                            onTriggerSearch={setCompanyNameSearch}
                            prsInInbox={prsInInbox}
                            setPrsInInbox={setPrsInInbox}
                            prsArchived={prsArchived}
                            setPrsArchived={setPrsArchived}
                            setSelectedCompany={setSelectedCompany}
                            selectedPR={selectedPR}
                            setSelectedPR={setSelectedPR}
                            onArchive={handleArchive}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <CompanyTargets
                            companies={companies}
                            selectedTargets={selectedTargets}
                            setSelectedTargets={setSelectedTargets}
                            associatedPRs={associatedPRs}
                            setAssociatedPRs={setAssociatedPRs}
                            sourceType={sourceType}
                            setSourceType={setSourceType}
                            companyNameSearch={companyNameSearch}
                            setCompanyNameSearch={setCompanyNameSearch}
                            selectedCompany={selectedCompany}
                            selectedPR={selectedPR}
                            onArchivePR={handleArchive}
                            onDisconnectCompany={handleDisconnectCompany}
                        />
                    </Grid>
                </Grid>
            ) : null}
            {tabValue === 'isin' ? (
                <IsinMap
                    unusedIsins={filteredUnusedIsins}
                    setUnusedIsins={setFilteredUnusedIsins}
                    startDate={isinStartDate}
                    setStartDate={setIsinStartDate}
                    onlyIsinsWithEvents={onlyIsinsWithEvents}
                    setOnlyIsinsWithEvents={setOnlyIsinsWithEvents}
                />
            ) : null}
        </>
    )
}

export default PRInput
